import type { CSSProperties } from 'react'

import styled from '@emotion/styled'
import { css } from '@mui/material'
import { Link } from 'react-router-dom'
import { theme } from 'styles/theme/hav-theme'

const COLLAPSED_WIDTH = '85px'
const EXPANDED_WIDTH = '346px'
const SUBMENU_WIDTH = '260px'
const TOPBAR_HEIGHT = '52px'
const TRANSITION_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

type WrapperProps = {
  collapsed?: boolean
  submenuOpen?: boolean
}

export const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100vh;
  z-index: 50;
  background-color: #00000050;
  min-width: ${({ collapsed, submenuOpen }) =>
    collapsed && !submenuOpen ? COLLAPSED_WIDTH : '100vw'};
`

type MenuSideBarWrapperProps = {
  collapsed: boolean
}

export const MenuSideBarWrapper = styled.aside<MenuSideBarWrapperProps>`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  width: ${({ collapsed }) => (collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)};
  border-right: 1px solid ${theme.colors.neutral.softer};

  margin-top: ${TOPBAR_HEIGHT};
  height: calc(100vh - ${TOPBAR_HEIGHT});
  background-color: ${theme.colors.neutral.white};
  transition: width 250ms ${TRANSITION_EASE};
`

export const Menu = styled.nav<WrapperProps>`
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  flex: 1;
  padding: 0 ${theme.spacing.md};

  ${({ collapsed }) =>
    collapsed &&
    css`
      padding: 0;
    `}
`

export const Footer = styled.ul`
  margin: 0;
  padding: ${theme.spacing.md};
  list-style: none;
`

type ContentProps = {
  hidden?: boolean
  collapsed?: boolean
}

const Content = styled.ul<ContentProps>`
  padding: 0;
  list-style: none;

  li + li,
  div + ul,
  span + li {
    margin-top: ${theme.spacing.md};
  }
`

export const MenuContent = styled(Content)`
  display: flex;
  flex-direction: column;

  ${({ collapsed }) =>
    collapsed &&
    css`
      align-items: center;
    `}
`

export const NodeContent = styled(Content)`
  ${({ hidden }) =>
    hidden &&
    css`
      display: none;
    `}

  ${({ collapsed }) =>
    collapsed &&
    css`
      position: fixed;
      top: 0;
      left: ${COLLAPSED_WIDTH};

      width: ${SUBMENU_WIDTH};
      height: calc(100vh - ${TOPBAR_HEIGHT});
      border-left: 1px solid ${theme.colors.neutral.softer};
      background-color: ${theme.colors.neutral.white};
      margin-top: ${TOPBAR_HEIGHT} !important;
      padding: ${theme.spacing.md};

      overflow-y: auto;
      overflow-x: hidden;
    `}
`

type MenuTitleProps = {
  collapsed?: boolean
}

export const MenuTitle = styled.span<MenuTitleProps>`
  font-weight: ${theme.typography.weight.sm};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.ion.state.enabled.neutral};
  padding-left: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};

  ${({ collapsed }) =>
    collapsed &&
    css`
      width: 100%;
      display: flex;
      justify-content: center;
      font-size: ${theme.typography.sizes['2xs']};
      padding-left: 0;
    `}
`

export const TitleSubmenu = styled.span`
  font-weight: ${theme.typography.weight.sm};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.ion.state.enabled.neutral};
  padding: 0 ${theme.spacing.sm};
`

export const Text = styled.span`
  font-weight: ${theme.typography.weight.sm};
  font-size: ${theme.typography.sizes.sm};
`

type FlexProps = {
  direction?: 'row' | 'column'
  alignItems?: CSSProperties['alignItems']
  justifyContent?: CSSProperties['justifyContent']
  gap?: keyof typeof theme.spacing
}

export const Flex = styled.div<FlexProps>`
  display: flex;
  flex-direction: ${({ direction = 'row' }) => direction};
  align-items: ${({ alignItems = 'baseline' }) => alignItems};
  justify-content: ${({ justifyContent = 'baseline' }) => justifyContent};
  gap: ${({ gap }) => (gap ? theme.spacing[gap] : 0)};
`

export const Links = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`

export const MenuItems = styled.div`
  margin-left: ${theme.spacing.md};
`

type ListItemProps = {
  hidden?: boolean
  selected?: boolean
  active?: boolean
  collapsed?: boolean
}

export const ListItem = styled.li<ListItemProps>`
  overflow: hidden;
  white-space: nowrap;
  margin: 0px ${theme.spacing.xs};

  display: ${({ hidden }) => (hidden ? 'none' : 'list-item')};

  * {
    text-decoration: none;
  }

  > div,
  > a > div {
    height: 44px;
    padding: 0 ${theme.spacing.sm};
    color: ${theme.colors.neutral.black};
    cursor: pointer;
    border-radius: ${theme.shapes.borderRadius.sm};
    justify-content: space-between;

    ${({ collapsed }) =>
      collapsed &&
      css`
        display: flex;
        align-items: center;
      `}

    ${({ active }) =>
      active &&
      css`
        color: ${theme.colors.ion.primary.main};

        svg path {
          fill: ${theme.colors.ion.primary.main};
        }
      `}

    ${({ selected }) =>
      selected &&
      css`
        color: ${theme.colors.ion.primary.main};
        background-color: ${theme.colors.ion.state.focus.ghost};

        svg path {
          fill: ${theme.colors.ion.primary.main};
        }
      `}

    &:hover {
      color: ${theme.colors.ion.primary.main};
      background-color: ${theme.colors.ion.state.focus.ghost};

      svg path {
        fill: ${theme.colors.ion.primary.main};
      }
    }
  }
`

export const ExpandButtonWrapper = styled.div<WrapperProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md};

  ${({ collapsed }) =>
    collapsed &&
    css`
      justify-content: center;
    `}
`

export const LogoWrapper = styled.div<WrapperProps>`
  display: flex;
  align-items: center;

  ${({ collapsed }) =>
    collapsed &&
    css`
      width: 0;
    `}
`

type ContainerProps = {
  collapsed: boolean
  disabled?: boolean
}

export const ExpandButton = styled.button<ContainerProps>`
  background-color: transparent;
  outline: none;
  border: none;
  cursor: pointer;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: 250ms ${TRANSITION_EASE};
  border: 1px solid ${theme.colors.neutral.softer};
  :hover {
    background-color: ${theme.colors.quartenary};
  }

  svg {
    transition: transform 250ms ${TRANSITION_EASE};
    transform: rotate(180deg);
    path {
      fill: ${theme.colors.primary.main};
    }
  }

  ${({ collapsed }) =>
    collapsed &&
    css`
      svg {
        transform: rotate(0deg);
      }
    `}
`
